import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, ArrowRightLeft, Layers, Users, Cpu, Zap, TrendingUp, Clock } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { StatCard } from '../components/common/StatCard';
import { DataTable } from '../components/common/DataTable';
import { HashDisplay } from '../components/common/HashDisplay';
import { LiveIndicator } from '../components/common/LiveIndicator';
import { getNetworkStats, getBlocks, getTransactions } from '../lib/api';
import { formatNumber, formatGG, formatHashrate, timeAgo, formatHash } from '../lib/utils';
import { Button } from '../components/ui/button';

export default function Home() {
  const [stats, setStats] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, blocksRes, txRes] = await Promise.all([
          getNetworkStats(),
          getBlocks(1, 10),
          getTransactions(1, 10),
        ]);
        setStats(statsRes.data);
        setBlocks(blocksRes.data);
        setTransactions(txRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const blockColumns = [
    {
      header: 'Height',
      cell: (row) => (
        <Link to={`/block/${row.height}`} className="text-primary hover:underline font-mono font-medium">
          #{formatNumber(row.height, 0)}
        </Link>
      ),
    },
    {
      header: 'Hash',
      cell: (row) => <HashDisplay hash={row.hash} link={`/block/${row.height}`} />,
    },
    {
      header: 'Txns',
      cell: (row) => <span className="font-mono">{row.tx_count}</span>,
    },
    {
      header: 'Reward',
      cell: (row) => (
        <span className="text-green-400 font-mono text-sm">
          {row.reward.total.toFixed(6)} GG
        </span>
      ),
    },
    {
      header: 'Time',
      cell: (row) => <span className="text-muted-foreground text-sm">{timeAgo(row.timestamp)}</span>,
    },
  ];

  const txColumns = [
    {
      header: 'Hash',
      cell: (row) => <HashDisplay hash={row.hash} link={`/tx/${row.hash}`} />,
    },
    {
      header: 'From',
      cell: (row) => <HashDisplay hash={row.from_address} type="address" link={`/address/${row.from_address}`} />,
    },
    {
      header: 'To',
      cell: (row) => <HashDisplay hash={row.to_address} type="address" link={`/address/${row.to_address}`} />,
    },
    {
      header: 'Value',
      cell: (row) => (
        <span className="text-primary font-mono text-sm">
          {formatNumber(row.value, 4)} GG
        </span>
      ),
    },
    {
      header: 'Time',
      cell: (row) => <span className="text-muted-foreground text-sm">{timeAgo(row.timestamp)}</span>,
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="home-page">
        {/* Hero Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black">
              GG <span className="gradient-text">CHAIN</span> Explorer
            </h1>
            <LiveIndicator />
          </div>
          <p className="text-muted-foreground text-lg max-w-2xl">
            Hybrid PoW (70%) + PoS (30%) Layer-1 blockchain with 21M max supply, 
            10-second blocks, and 40-year mining schedule.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12" data-testid="stats-grid">
          <StatCard
            title="Block Height"
            value={loading ? '...' : formatNumber(stats?.current_height, 0)}
            subtitle={`Epoch ${stats?.current_epoch || '-'} / 10`}
            icon={Layers}
            accentColor="primary"
          />
          <StatCard
            title="Network Hashrate"
            value={loading ? '...' : formatHashrate(stats?.hashrate)}
            subtitle="PoW Mining Power"
            icon={Cpu}
            accentColor="secondary"
          />
          <StatCard
            title="Circulating Supply"
            value={loading ? '...' : formatNumber(stats?.circulating_supply, 0)}
            subtitle={`of ${formatNumber(stats?.total_supply, 0)} GG`}
            icon={TrendingUp}
            accentColor="success"
          />
          <StatCard
            title="Validators"
            value={loading ? '...' : formatNumber(stats?.validators_count, 0)}
            subtitle={`${formatNumber(stats?.staked_supply, 0)} GG staked`}
            icon={Users}
            accentColor="warning"
          />
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="stat-card">
            <p className="text-xs text-muted-foreground mb-1">Block Time</p>
            <p className="text-xl font-bold font-mono">{stats?.avg_block_time || 10}s</p>
          </div>
          <div className="stat-card">
            <p className="text-xs text-muted-foreground mb-1">Difficulty</p>
            <p className="text-xl font-bold font-mono">{formatNumber(stats?.difficulty, 0)}</p>
          </div>
          <div className="stat-card">
            <p className="text-xs text-muted-foreground mb-1">Mempool</p>
            <p className="text-xl font-bold font-mono">{stats?.mempool_size || 0} txs</p>
          </div>
          <div className="stat-card">
            <p className="text-xs text-muted-foreground mb-1">Total Txns</p>
            <p className="text-xl font-bold font-mono">{formatNumber(stats?.total_transactions, 0)}</p>
          </div>
        </div>

        {/* Latest Data */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Latest Blocks */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Box className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-bold">Latest Blocks</h2>
              </div>
              <Link to="/blocks">
                <Button variant="ghost" size="sm" className="text-primary hover:text-primary/80">
                  View All
                </Button>
              </Link>
            </div>
            <DataTable
              columns={blockColumns}
              data={blocks}
              loading={loading}
              emptyMessage="No blocks found"
            />
          </div>

          {/* Latest Transactions */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <ArrowRightLeft className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-bold">Latest Transactions</h2>
              </div>
              <Link to="/transactions">
                <Button variant="ghost" size="sm" className="text-primary hover:text-primary/80">
                  View All
                </Button>
              </Link>
            </div>
            <DataTable
              columns={txColumns}
              data={transactions}
              loading={loading}
              emptyMessage="No transactions found"
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}
